// Cross-promote strip — surfaces 3 recent VendAgain for-sale listings on
// pages where they're relevant (VendTrade detail today, easily reused
// elsewhere). Filters to the current city when provided so the strip
// stays locally relevant.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Package } from "lucide-react";
import api from "@/lib/api";
import { CategoryChip, formatPrice, CAT_MAP } from "@/pages/vendagain/shared";

export default function VendAgainCrossPromote({
  city = "",
  excludeUserId = null,
  limit = 3,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = { limit: Math.max(limit, 4) };
        if (city) params.city = city;
        const { data } = await api.get("/vendagain/listings", { params });
        let list = data.items || [];
        if (excludeUserId) {
          list = list.filter((i) => i.owner?.id !== excludeUserId);
        }
        // If a city filter came back empty, retry without it so the strip
        // still has something to show.
        if (list.length === 0 && city) {
          const { data: fallback } = await api.get("/vendagain/listings", {
            params: { limit: Math.max(limit, 4) },
          });
          list = (fallback.items || []).filter(
            (i) => !excludeUserId || i.owner?.id !== excludeUserId,
          );
        }
        if (alive) setItems(list.slice(0, limit));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [city, excludeUserId, limit]);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="mt-14 pt-10 border-t"
      style={{ borderColor: "rgba(0,0,0,0.08)" }}
      data-testid="vendagain-crosspromote"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>
            You might also like on VendAgain
          </div>
          <p className="text-sm opacity-70">
            Gear, furniture, and gently-used items posted by the community.
          </p>
        </div>
        <Link
          to="/vendagain"
          className="text-sm font-bold inline-flex items-center gap-1 hover:opacity-70"
          style={{ color: "var(--avt-purple)" }}
          data-testid="vendagain-crosspromote-more"
        >
          Browse all <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((i) => {
          const cat = CAT_MAP[i.category];
          const Icon = cat?.icon;
          return (
            <Link
              key={i.id}
              to={`/vendagain/listings/${i.id}`}
              className="card-warm rounded-sm overflow-hidden block group"
              data-testid={`vendagain-crosspromote-card-${i.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[color:var(--sand)] relative">
                {i.image_urls && i.image_urls[0] ? (
                  <img
                    src={i.image_urls[0]}
                    alt={i.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {Icon ? (
                      <Icon className="h-10 w-10 opacity-30" style={{ color: cat.ink }} />
                    ) : (
                      <Package className="h-10 w-10 opacity-20" />
                    )}
                  </div>
                )}
                <span
                  className="absolute top-2 right-2 text-sm font-bold px-3 py-1 rounded-full"
                  style={{ background: "rgba(255,255,255,0.95)", color: "var(--ink)" }}
                >
                  {formatPrice(i.price_cents, i.best_offer)}
                </span>
              </div>
              <div className="p-4">
                <CategoryChip value={i.category} />
                <h3 className="font-serif text-lg mt-2 leading-tight line-clamp-2">
                  {i.title}
                </h3>
                <div className="mt-2 text-xs opacity-70 inline-flex items-center gap-1">
                  <MapPin size={11} /> {i.city}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
